import {
  CloseOutlined,
  ExpandAltOutlined,
  SendOutlined,
  ShrinkOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { ApiService } from '@services/ApiService'
import assistantStore, {
  AssistantMentionResult,
  AssistantMentionType,
} from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { pageContextNoun } from './assistantPageContext'
import { useAssistantSession } from './AssistantSessionContext'
import { getMentionAtCaret } from './composerMention'
import { unavailableText } from './errorMessages'
import { useAssistantPlacement } from './placements/PlacementContext'
import { KW } from './ui'
import { useVoiceInput } from './voice/useVoiceInput'
import VoiceInputControls from './voice/VoiceInputControls'
import { isVoiceRecordingSupported } from './voice/voiceRecorder'

/*
 * Composer editor. Mentions use the canonical token "@type: display name":
 *   "@"             → type picker (@specialist, @cm, …) + combined search from 2 chars
 *                     (a kind can ask for more: record numbers are searched from their
 *                     start, from 3 chars — see AssistantMentionType.minQueryLength)
 *   "@type: query"  → the text after ": " behaves like an inline input for that type —
 *                     it is rendered with a light input-style border (backdrop layer)
 *                     and typing in it (anywhere in the text, caret-aware) drives the
 *                     per-type search dropdown.
 * Completed mentions render as teal pills behind the text. Refs live in
 * the panel's session (useAssistantSession) draftMentions and are re-validated against the text on send.
 *
 * The text itself lives in the session's draftText: this component is unmounted while the
 * Guide or the History is shown, and the draft must still be there afterwards. It can be typed
 * while the assistant answers; only sending waits for the answer.
 *
 * Above the editor, the Context chip names the record a question is asked about when it says
 * "this case" / "this report" (session.activePageContext): what is on the chip is what is
 * sent, and removing the chip sends the next questions without it.
 *
 * Voice input (the microphone, when assistantStore.voiceInput is on and the browser can record):
 * the question is spoken, the server transcribes it, and the text is put at the caret for the
 * user to read over — it is never sent by itself. Escape while recording cancels it.
 *
 * What the composer offers is the placement's (useAssistantPlacement().composer); the drawer has
 * all of it. Without slashCommands there is no "/" button or menu and "/name" is sent as plain
 * text; without mentions there is no "@" button and typing "@" opens nothing; a 'fixed' Context
 * chip has no remove button.
 */

const COLLAPSED_MAX_HEIGHT = 120
const EXPANDED_HEIGHT = 200
const DEFAULT_MIN_MENTION_QUERY = 2
const MENTION_DEBOUNCE_MS = 250

interface Props {
  /**
   * The composer is on screen. The drawer's composer stays mounted while the drawer is closed, so
   * it follows the drawer (the default); a pane that shows the Assistant renders it only while
   * the pane is open, and says so.
   */
  visible?: boolean
}

const Composer = ({ visible }: Props) => {
  const session = useAssistantSession()
  const { composer: features } = useAssistantPlacement()
  const value = session.draftText
  const setValue = session.editDraft
  const [caret, setCaret] = useState(() => session.draftText.length)
  const [expanded, setExpanded] = useState(false)
  const [sendHover, setSendHover] = useState(false)
  const [dismissedToken, setDismissedToken] = useState<string | null>(null)
  const [mentionResults, setMentionResults] = useState<
    AssistantMentionResult[]
  >([])
  const [mentionLoading, setMentionLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const pendingCaretRef = useRef<number | null>(null)
  // The pending caret was set for a draft the store put in place, not for something the user
  // did in the composer: the focus is then only taken when it is free (see focusIsFree).
  const pendingCaretFromStoreRef = useRef(false)
  const menuRef = useRef<HTMLDivElement>(null)
  // Set by the arrow keys only, so hovering an item never scrolls the list.
  const keyboardNavRef = useRef(false)
  const [canRecord] = useState(isVoiceRecordingSupported)

  // Opening the drawer (button or Ctrl+K) or the pane puts the cursor straight into the composer.
  const isOpen = visible ?? assistantStore.isOpen
  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus()
    }
  }, [isOpen])

  // A draft put in place by the store (a prefill, an example from the guide, a question handed
  // back) and the one found on mount (back from the Guide or the History): caret at its end.
  const draftRevision = session.draftRevision
  const mountedRef = useRef(false)
  useEffect(() => {
    const end = session.draftText.length
    if (mountedRef.current || end > 0) {
      setCaret(end)
      pendingCaretRef.current = end
      pendingCaretFromStoreRef.current = true
    }
    mountedRef.current = true
    // Only a new revision moves the caret: typing changes the text, not the revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRevision])

  /**
   * The focus is nowhere (body) or already inside the assistant's panel. The drawer has no mask:
   * the user may be typing in a field of the page behind it, and that field keeps the focus.
   */
  const focusIsFree = () => {
    const focused = document.activeElement
    const panel = textareaRef.current?.closest('[data-assistant-panel]')
    return !focused || focused === document.body || !!panel?.contains(focused)
  }

  // A control of the panel that removed itself when it was used (the Context chip's remove
  // button, "Jump to latest") asks for the cursor here, instead of leaving the focus nowhere.
  const focusRequest = session.composerFocusRequest
  const focusRequestRef = useRef(focusRequest)
  useEffect(() => {
    if (focusRequestRef.current === focusRequest) {
      return
    }
    focusRequestRef.current = focusRequest
    textareaRef.current?.focus()
  }, [focusRequest])

  // When the answer has ended (or a confirm/cancel has been applied, or a conversation has
  // loaded) the cursor is back in the composer, ready for the next question — unless the user
  // is busy somewhere else: in a field of the page behind the drawer, or selecting text.
  const locked = session.isInputLocked
  const wasLockedRef = useRef(locked)
  useEffect(() => {
    const wasLocked = wasLockedRef.current
    wasLockedRef.current = locked
    const el = textareaRef.current
    if (!wasLocked || locked || !el || !isOpen) {
      return
    }
    const selection = window.getSelection?.()
    if (selection && !selection.isCollapsed) {
      return
    }
    if (focusIsFree()) {
      el.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked])

  // Manual autosize (collapsed) / fixed height (expanded)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) {
      return
    }
    if (expanded) {
      el.style.height = `${EXPANDED_HEIGHT}px`
      return
    }
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, COLLAPSED_MAX_HEIGHT)}px`
  }, [value, expanded])

  // Restore the caret after programmatic inserts (type/result picks)
  useEffect(() => {
    if (pendingCaretRef.current == null) {
      return
    }
    const el = textareaRef.current
    if (el) {
      // A question the store handed back (a conversation that could not be started, an answer
      // that was refused) arrives whenever the server answers: it must not pull the cursor out
      // of a field of the page the user has gone on typing in meanwhile.
      if (!pendingCaretFromStoreRef.current || focusIsFree()) {
        el.focus()
      }
      el.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current)
    }
    pendingCaretRef.current = null
    pendingCaretFromStoreRef.current = false
  })

  const syncCaret = () => {
    const el = textareaRef.current
    if (el) {
      setCaret(el.selectionStart ?? 0)
    }
  }

  // ---- mention state machine (caret-aware) -----------------------------------
  const isCompletedDisplay = (segment: string) =>
    session.draftMentions.some(m => segment.startsWith(m.display))

  // Without mentions the state machine never starts: "@" is a character like any other.
  const activeMention = features.mentions
    ? getMentionAtCaret(value, caret, isCompletedDisplay)
    : null
  const mentionToken = activeMention
    ? value.slice(activeMention.start, caret)
    : null
  const mentionActive = !!activeMention && mentionToken !== dismissedToken
  const inSearchStage = mentionActive && activeMention!.stage === 'search'
  const typeToken = activeMention?.typeToken ?? ''

  const matchedType: AssistantMentionType | undefined = inSearchStage
    ? assistantStore.mentionTypes.find(
        t =>
          t.type.toLowerCase() === typeToken.toLowerCase() ||
          t.aliases.some(a => a.toLowerCase() === typeToken.toLowerCase())
      )
    : undefined

  const typeItems =
    mentionActive && !inSearchStage
      ? assistantStore.mentionTypes.filter(
          t =>
            !typeToken ||
            t.type.toLowerCase().startsWith(typeToken.toLowerCase()) ||
            t.aliases.some(a =>
              a.toLowerCase().startsWith(typeToken.toLowerCase())
            )
        )
      : []

  const effectiveQuery = mentionActive ? activeMention!.query.trim() : ''
  const minQueryLength =
    matchedType?.minQueryLength ?? DEFAULT_MIN_MENTION_QUERY
  const shouldFetch = mentionActive && effectiveQuery.length >= minQueryLength

  useEffect(() => {
    if (!shouldFetch) {
      setMentionResults([])
      setMentionLoading(false)
      return
    }
    setMentionLoading(true)
    // One request per settled text: the next keystroke cancels the timer of a request not yet
    // sent and aborts one already on its way, so an older answer can never replace a newer one.
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const res = await ApiService.Assistant.SearchMentions(
          {
            type: inSearchStage ? matchedType?.type : undefined,
            query: effectiveQuery,
            top: 8,
          },
          controller.signal
        )
        if (controller.signal.aborted) {
          return
        }
        setMentionResults(res.data ?? [])
        setMentionLoading(false)
      } catch {
        // An abandoned request is not a failed search: the request that replaced it owns the list.
        if (controller.signal.aborted) {
          return
        }
        setMentionResults([])
        setMentionLoading(false)
      }
    }, MENTION_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, inSearchStage, matchedType?.type, effectiveQuery])

  const replaceActiveToken = (replacement: string) => {
    if (!activeMention) {
      return
    }
    const before = value.slice(0, activeMention.start)
    const after = value.slice(caret)
    const next = before + replacement + after
    setValue(next)
    const newCaret = before.length + replacement.length
    setCaret(newCaret)
    pendingCaretRef.current = newCaret
  }

  const pickType = (t: AssistantMentionType) => {
    replaceActiveToken(`@${t.type}: `)
    setActiveIndex(0)
  }

  const pickResult = (r: AssistantMentionResult) => {
    const display = `@${r.entityType}: ${r.label}`
    replaceActiveToken(`${display} `)
    session.registerDraftMention({
      display,
      entityType: r.entityType,
      id: r.id,
      label: r.label,
    })
    setMentionResults([])
    setActiveIndex(0)
  }

  const mentionItems: (
    | { kind: 'type'; type: AssistantMentionType }
    | { kind: 'result'; result: AssistantMentionResult }
  )[] = mentionActive
    ? [
        ...typeItems.map(t => ({ kind: 'type' as const, type: t })),
        ...mentionResults.map(r => ({ kind: 'result' as const, result: r })),
      ]
    : []

  const pickMentionItem = (item: (typeof mentionItems)[number]) => {
    if (item.kind === 'type') {
      pickType(item.type)
    } else {
      pickResult(item.result)
    }
  }

  // ---- send / commands ---------------------------------------------------------
  const send = () => {
    // Locked (an answer is coming in): the text stays where it is, to be sent afterwards.
    if (!value.trim() || session.isInputLocked) {
      return
    }
    setCaret(0)
    // fromComposer: the store empties the draft, and hands the text back if it never reaches
    // the server.
    session.sendMessage(value, {
      fromComposer: true,
      commands: features.slashCommands,
    })
  }

  // Without slash commands "/" opens no menu: the text is sent as it is typed.
  const commandQuery =
    features.slashCommands &&
    !mentionActive &&
    value.startsWith('/') &&
    !/\s/.test(value)
      ? value.slice(1).toLowerCase()
      : null
  const matchingCommands =
    commandQuery !== null
      ? assistantStore.commands.filter(c =>
          c.name.toLowerCase().startsWith(commandQuery)
        )
      : []

  const pickCommand = (name: string) => {
    const next = `/${name} `
    setValue(next)
    setCaret(next.length)
    pendingCaretRef.current = next.length
  }

  /** Toolbar "/" and "@": types the trigger for the user so its menu opens. */
  const insertTrigger = (trigger: '/' | '@') => {
    let next: string
    let nextCaret: number
    if (trigger === '/') {
      // Commands only work at the start of an empty message.
      next = '/'
      nextCaret = 1
    } else {
      const at = textareaRef.current?.selectionStart ?? value.length
      const before = value.slice(0, at)
      const insert = before && !/\s$/.test(before) ? ' @' : '@'
      next = before + insert + value.slice(at)
      nextCaret = at + insert.length
    }
    setValue(next)
    setCaret(nextCaret)
    setDismissedToken(null)
    pendingCaretRef.current = nextCaret
  }

  /** Voice input: the transcript goes in at the caret, apart from the words around it. */
  const insertTranscript = (text: string) => {
    const at = Math.min(
      textareaRef.current?.selectionStart ?? value.length,
      value.length
    )
    const before = value.slice(0, at)
    const after = value.slice(at)
    const lead = before && !/\s$/.test(before) ? ' ' : ''
    const trail = after && !/^\s/.test(after) ? ' ' : ''
    setValue(before + lead + text + trail + after)
    const nextCaret = at + lead.length + text.length
    setCaret(nextCaret)
    setDismissedToken(null)
    pendingCaretRef.current = nextCaret
    // It arrives whenever the server answers: a page field the user moved to keeps the focus.
    pendingCaretFromStoreRef.current = true
  }

  const voice = useVoiceInput({
    maxSeconds: assistantStore.voiceMaxSeconds,
    onTranscript: insertTranscript,
    onUnavailable: assistantStore.reloadCapabilities,
  })
  const voiceActive = voice.state !== 'idle'

  // The drawer stays mounted when it closes: closing it ends voice input, so nothing is recorded
  // or sent while the assistant is hidden.
  useEffect(() => {
    if (!isOpen && voiceActive) {
      voice.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Escape while recording (or transcribing) cancels it — and only that: the drawer stays open
  // and an open mention list stays as it is.
  const onKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && voiceActive) {
      e.preventDefault()
      e.stopPropagation()
      voice.cancel()
    }
  }

  useEffect(() => {
    setActiveIndex(0)
  }, [mentionItems.length, inSearchStage, matchingCommands.length])

  // Keep the item focused with the arrow keys in view (commands and mentions).
  useEffect(() => {
    if (!keyboardNavRef.current) {
      return
    }
    keyboardNavRef.current = false
    menuRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // Tab and Enter both pick the focused item — mentions and slash commands alike.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionActive && mentionItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        keyboardNavRef.current = true
        setActiveIndex(i => (i + 1) % mentionItems.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        keyboardNavRef.current = true
        setActiveIndex(i => (i - 1 + mentionItems.length) % mentionItems.length)
        return
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault()
        pickMentionItem(mentionItems[activeIndex] ?? mentionItems[0])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setDismissedToken(mentionToken)
      }
      return
    }

    if (matchingCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        keyboardNavRef.current = true
        setActiveIndex(i => (i + 1) % matchingCommands.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        keyboardNavRef.current = true
        setActiveIndex(
          i => (i - 1 + matchingCommands.length) % matchingCommands.length
        )
        return
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault()
        pickCommand((matchingCommands[activeIndex] ?? matchingCommands[0]).name)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  // ---- backdrop highlight segments ---------------------------------------------
  const renderBackdrop = (): ReactNode[] => {
    const ranges: { start: number; end: number; cls: string }[] = []
    session.draftMentions.forEach(m => {
      let idx = 0
      while ((idx = value.indexOf(m.display, idx)) !== -1) {
        ranges.push({
          start: idx,
          end: idx + m.display.length,
          cls: 'kw-mention-final',
        })
        idx += m.display.length
      }
    })
    if (mentionActive && activeMention) {
      ranges.push({
        start: activeMention.start,
        end: caret,
        cls: 'kw-mention-active',
      })
    }
    ranges.sort((a, b) => a.start - b.start)

    const out: ReactNode[] = []
    let pos = 0
    ranges.forEach((r, i) => {
      if (r.start < pos) {
        return
      }
      if (r.start > pos) {
        out.push(<span key={`p${i}`}>{value.slice(pos, r.start)}</span>)
      }
      out.push(
        <span key={`m${i}`} className={r.cls}>
          {value.slice(r.start, r.end)}
        </span>
      )
      pos = r.end
    })
    out.push(<span key="tail">{value.slice(pos)}&#10;</span>)
    return out
  }

  // Stop belongs to a running turn. While a conversation loads or a confirm/cancel is being
  // applied sending is only locked: there is nothing of the user's to stop. Typing stays
  // possible in all of these; it is only switched off while the assistant is not available.
  const running = session.isTurnRunning
  const busy = locked
  const unavailableReason = assistantStore.unavailableReason
  const pageContext =
    unavailableReason === null ? session.activePageContext : null
  const contextIsList = !!pageContext?.list
  const contextNoun = pageContext ? pageContextNoun(pageContext.type) : ''
  // A list is named by what the Assistant will assume ("IME cases list").
  const contextLabel =
    pageContext?.label ?? (contextIsList ? 'This list' : `This ${contextNoun}`)
  // Shown while a recording is going on even if the capabilities changed meanwhile, so it can
  // still be stopped or cancelled.
  const showVoice =
    canRecord && ((features.voice && assistantStore.voiceInput) || voiceActive)
  const removableContext = features.contextChip === 'removable'
  const showMentionDropdown =
    mentionActive &&
    (mentionItems.length > 0 ||
      mentionLoading ||
      (inSearchStage && effectiveQuery.length < minQueryLength))

  return (
    <div
      className="relative flex-shrink-0 px-4 pb-3 pt-[10px]"
      style={{ borderTop: `1px solid ${KW.line}` }}
      onKeyDownCapture={onKeyDownCapture}
    >
      {(showMentionDropdown || matchingCommands.length > 0) && (
        <div
          ref={menuRef}
          className="kw-scroll absolute left-4 right-4 z-10 max-h-[264px] overflow-y-auto rounded-xl bg-white p-[6px]"
          style={{
            bottom: 'calc(100% - 6px)',
            border: `1px solid ${KW.line2}`,
            boxShadow: '0 -8px 28px rgba(20,40,70,.14)',
          }}
        >
          {showMentionDropdown ? (
            <>
              {mentionItems.map((item, index) => (
                <button
                  key={
                    item.kind === 'type'
                      ? `t-${item.type.type}`
                      : `r-${item.result.entityType}-${item.result.id}`
                  }
                  type="button"
                  data-active={index === activeIndex}
                  className="block w-full cursor-pointer rounded-lg border-0 px-[10px] py-[9px] text-left"
                  style={{
                    background:
                      index === activeIndex ? '#f4fbfd' : 'transparent',
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pickMentionItem(item)}
                >
                  {item.kind === 'type' ? (
                    <>
                      <span
                        className="text-[13.5px] font-extrabold"
                        style={{ color: KW.teal500 }}
                      >
                        @{item.type.type}
                      </span>
                      <span
                        className="ml-2 text-xs"
                        style={{ color: KW.muted }}
                      >
                        {item.type.displayName}
                        {item.type.aliases.length
                          ? ` · @${item.type.aliases.join(', @')}`
                          : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <div
                        className="text-[13.5px] font-bold"
                        style={{ color: KW.ink }}
                      >
                        {item.result.label}
                      </div>
                      <div
                        className="mt-px text-xs"
                        style={{ color: KW.muted }}
                      >
                        {item.result.entityType.replace('_', ' ')}
                        {item.result.description
                          ? ` · ${item.result.description}`
                          : ''}
                      </div>
                    </>
                  )}
                </button>
              ))}
              {mentionLoading && (
                <div
                  className="px-[10px] py-[9px] text-xs"
                  style={{ color: KW.muted }}
                >
                  Searching…
                </div>
              )}
              {inSearchStage &&
                !mentionLoading &&
                mentionItems.length === 0 && (
                  <div
                    className="px-[10px] py-[9px] text-xs"
                    style={{ color: KW.muted }}
                  >
                    {effectiveQuery.length < minQueryLength
                      ? matchedType?.searchHint
                        ? `${matchedType.searchHint}…`
                        : `Type to search ${
                            matchedType?.displayName ?? 'records'
                          }…`
                      : 'No matches'}
                  </div>
                )}
            </>
          ) : (
            matchingCommands.map((command, index) => (
              <button
                key={command.name}
                type="button"
                data-active={index === activeIndex}
                className="block w-full cursor-pointer rounded-lg border-0 px-[10px] py-[9px] text-left"
                style={{
                  background: index === activeIndex ? '#f4fbfd' : 'transparent',
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => pickCommand(command.name)}
              >
                <div
                  className="text-[13.5px] font-extrabold"
                  style={{ color: KW.teal500 }}
                >
                  /{command.name}
                </div>
                <div className="mt-px text-xs" style={{ color: KW.muted }}>
                  {command.description}
                  {command.usageHint ? ` · ${command.usageHint}` : ''}
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {pageContext && (
        <div className="mb-[6px] flex">
          <span
            className={
              removableContext
                ? 'inline-flex max-w-full items-center gap-[6px] rounded-full py-[2px] pl-[10px] pr-[4px] text-xs'
                : // No remove button: the chip ends with the same space it starts with.
                  'inline-flex max-w-full items-center gap-[6px] rounded-full px-[10px] py-[3px] text-xs'
            }
            style={{
              background: '#f4fbfd',
              border: `1px solid ${KW.line2}`,
              color: KW.ink,
            }}
            title={
              contextIsList
                ? `The Assistant assumes your question is about the ${contextLabel}${
                    pageContext.list?.serviceType
                      ? ', so it does not ask for the case type'
                      : ''
                  }. It does not know which filters the list has on.`
                : `"This ${contextNoun}" in your question means this record`
            }
          >
            <span style={{ color: KW.muted }}>Context:</span>
            <span className="truncate font-bold">{contextLabel}</span>
            {removableContext && (
              <button
                type="button"
                aria-label={`Remove context ${contextLabel}`}
                title={
                  contextIsList
                    ? 'Ask without this list'
                    : 'Ask without this record'
                }
                onClick={() => {
                  session.dismissPageContext()
                  // The chip is gone with its button: the cursor goes to the composer.
                  textareaRef.current?.focus()
                }}
                className="flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-[10px]"
                style={{ color: KW.muted }}
              >
                <CloseOutlined />
              </button>
            )}
          </span>
        </div>
      )}
      {!pageContext &&
        unavailableReason === null &&
        session.pageContextRefused && (
          <div
            role="status"
            className="mb-[6px] text-[11.5px]"
            style={{ color: KW.muted }}
          >
            {session.pageContextRefusedIsList
              ? 'The list on this screen is not available to the Assistant, so it is not used as context.'
              : 'The record on this screen is not available to the Assistant, so it is not used as context.'}
          </div>
        )}

      <div className={`kw-composer${busy ? ' is-busy' : ''}`}>
        <div className="kw-editor">
          <div
            ref={backdropRef}
            className="kw-editor-backdrop kw-editor-text"
            aria-hidden
          >
            {renderBackdrop()}
          </div>
          <textarea
            ref={textareaRef}
            className="kw-editor-text"
            rows={1}
            placeholder={
              unavailableReason
                ? unavailableText(unavailableReason)
                : features.placeholder
            }
            aria-label="Message the Assistant"
            value={value}
            disabled={unavailableReason !== null}
            onChange={e => {
              setValue(e.target.value)
              setCaret(e.target.selectionStart ?? 0)
              setDismissedToken(null)
              if (voice.error) {
                voice.clearError()
              }
            }}
            onKeyDown={onKeyDown}
            onKeyUp={syncCaret}
            onClick={syncCaret}
            onSelect={syncCaret}
            onScroll={e => {
              if (backdropRef.current) {
                backdropRef.current.scrollTop = e.currentTarget.scrollTop
              }
            }}
          />
        </div>

        <div className="kw-composer-toolbar">
          {features.slashCommands && (
            <button
              type="button"
              className="kw-composer-tool"
              title={
                value.trim()
                  ? 'Commands — start an empty message with /'
                  : 'Commands'
              }
              disabled={busy || !!value.trim()}
              onClick={() => insertTrigger('/')}
            >
              /
            </button>
          )}
          {features.mentions && (
            <button
              type="button"
              className="kw-composer-tool"
              title="Tag a case, company, person…"
              disabled={busy}
              onClick={() => insertTrigger('@')}
            >
              @
            </button>
          )}
          {/* Kept without the keyboard hint: it also says how voice input is getting on. */}
          <span className="kw-composer-hint" aria-live="polite">
            {voice.state === 'starting'
              ? 'Waiting for the microphone…'
              : voice.state === 'recording'
              ? 'Listening… stop to see the text'
              : voice.state === 'transcribing'
              ? 'Transcribing…'
              : features.keyboardHint
              ? 'Enter to send · Shift+Enter for a new line'
              : ''}
          </span>
          <div className="flex-1" />
          {features.expand && (
            <button
              type="button"
              className="kw-composer-tool"
              title={expanded ? 'Collapse' : 'Expand'}
              onClick={() => setExpanded(x => !x)}
            >
              {expanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
            </button>
          )}
          {showVoice && (
            <VoiceInputControls
              voice={voice}
              disabled={unavailableReason !== null}
            />
          )}
          <button
            type="button"
            title={running ? 'Stop' : 'Send'}
            aria-label={running ? 'Stop' : 'Send'}
            onClick={running ? session.stop : send}
            onMouseEnter={() => setSendHover(true)}
            onMouseLeave={() => setSendHover(false)}
            disabled={!running && (busy || !value.trim())}
            className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-[9px] border-0 text-white"
            style={{
              background: running
                ? KW.red
                : busy || !value.trim()
                ? '#c9d4dd'
                : sendHover
                ? '#12aecb'
                : KW.teal,
              transition: 'background .12s',
            }}
          >
            {running ? <StopOutlined /> : <SendOutlined />}
          </button>
        </div>
      </div>
      {voice.error && (
        <div
          role="alert"
          className="mt-[6px] text-[11.5px]"
          style={{ color: KW.red }}
        >
          {voice.error}
        </div>
      )}
      <div
        className="mt-[6px] text-center text-[11px]"
        style={{ color: '#98a2b3' }}
      >
        AI can make mistakes. Verify information before using it.
      </div>
    </div>
  )
}

export default observer(Composer)
